/**
 * The open document's Export ▸ handlers (card 3.2; moved out of
 * DocumentEditorTab.js). The per-format converters stay in
 * ../documentExport.js and ../documentPdf.js — this module decides WHAT is
 * exported (one page, or a page with its sub-pages) and in which container:
 *
 *  - PDF: one file; sub-pages follow as sections, each on a new page.
 *  - Markdown: the git sync Stage 2 serializer (front matter + exact Markdown),
 *    so an exported file reads back as the same document. With sub-pages, a
 *    zip laid out like the git repository (a page's sub-pages in a folder named
 *    after it) with relative links between the pages.
 *  - HTML: one self-contained file; with sub-pages a zip, links relative.
 */
import i18n from '../../i18n';
import { listTree, readDocument } from '../../extensions/bundled/documents/documentsStore';
import { bucketScopeOf } from '../../extensions/bundled/documents/documentsScopeTree';
import { collectSubtree, indexNodes } from '../../extensions/bundled/documents/documentsTree';
import { documentToMarkdown as exactMarkdown } from '../../utils/docMarkdown';
import { htmlToBlocks } from '../../utils/docMarkdown/fromHtml';
import { documentFile, layoutDocuments, relativeHref, resolveHref } from '../../services/git/scopeDocumentsLayout';
import { resolveExportBodyHtml } from '../../utils/resolveDocMediaForExport';
import { saveTextFile } from '../../utils/saveTextFile';
import { zipStore } from '../documents-sidebar/zipStore';
import { documentToHtmlFile, documentToMarkdown as legacyMarkdown, exportDocument as exportSingle, saveBinaryFile } from '../documentExport';

const bySortKey = (a, b) => String(a.sortKey).localeCompare(String(b.sortKey)) || String(a.id).localeCompare(String(b.id));

export function exportFileBase(name) {
  return (
    String(name || 'document')
      .trim()
      .replace(/[^\w.\- ]+/g, '')
      .replace(/\s+/g, '-')
      .replace(/^-+|-+$/g, '') || 'document'
  );
}

/**
 * One page as Markdown with front matter. `paths` (zip export) maps doc ids
 * to their file paths so page links between exported pages become relative
 * links that read back as the same links.
 */
export function markdownFileFor(node, doc, { file = null, paths = null } = {}) {
  const blocks = doc.bodyLexical || htmlToBlocks(doc.bodyHtml || '');
  const byPath = paths ? new Map([...paths].map(([id, p]) => [p, id])) : null;
  const ctx =
    file && paths
      ? {
          pageLinkHref: (n) => (paths.has(String(n.docId)) ? relativeHref(file, paths.get(String(n.docId))) : undefined),
          pageLinkFromHref: (href) => {
            const target = resolveHref(file, href);
            const id = target && byPath.get(target);
            return id ? { docId: id, scopeType: 'personal', teamId: null } : null;
          },
        }
      : {};
  if (ctx.pageLinkHref) {
    // Fall back to the default apiboo:// link for pages outside the export.
    const own = ctx.pageLinkHref;
    ctx.pageLinkHref = (n) => own(n) || `apiboo://documents/${n.scopeType === 'team' ? `team:${n.teamId}` : n.scopeType || 'personal'}/${n.docId}`;
  }
  let body;
  try {
    body = exactMarkdown(blocks, ctx);
  } catch {
    // No exact form (rare): the readable HTML-based Markdown, without its title line.
    body = legacyMarkdown('', doc.bodyHtml || '');
  }
  return documentFile({ id: node.id, name: doc.title ?? node.name, sortKey: node.sortKey || '', tags: node.tags }, body);
}

/** The page and — with `includeSubpages` — every sub-page below it, in tree order, with bodies. */
async function collectPages({ documentId, scope, snapshot, includeSubpages }) {
  const bucket = bucketScopeOf(scope);
  let nodes = [];
  try {
    nodes = await listTree(bucket);
  } catch {
    nodes = [];
  }
  const index = indexNodes(nodes, bySortKey);
  const root = index.nodeById.get(String(documentId)) || { id: documentId, type: 'document', name: snapshot.title, sortKey: '' };
  const list = includeSubpages ? collectSubtree(index, documentId).filter((n) => n.type === 'document') : [root];
  if (!list.length) list.push(root);
  const pages = [];
  for (const node of list) {
    const own = String(node.id) === String(documentId);
    // eslint-disable-next-line no-await-in-loop
    const doc = own ? snapshot : await readDocument(node.id, bucket);
    if (doc) pages.push({ node: { ...node, parentId: own ? null : node.parentId }, doc: { ...doc, title: doc.title || node.name } });
  }
  return pages;
}

/** Repository-style paths for the pages, without the `documents/` prefix. */
function pagePaths(pages, ext) {
  const layout = layoutDocuments(pages.map((p) => ({ ...p.node, name: p.doc.title })));
  const out = new Map();
  for (const p of pages) out.set(String(p.node.id), layout.get(String(p.node.id)).file.replace(/^documents\//, '').replace(/\.md$/, ext));
  return out;
}

async function combinedPdf(pages) {
  const { loadPdfMake, buildPdfDocDefinition, documentBodyContent } = await import('../documentPdf');
  const pdfMake = await loadPdfMake();
  const [first, ...rest] = pages;
  const def = buildPdfDocDefinition({ title: first.doc.title, bodyHtml: await resolveExportBodyHtml(first.doc.bodyHtml) });
  for (const p of rest) {
    // eslint-disable-next-line no-await-in-loop
    const html = await resolveExportBodyHtml(p.doc.bodyHtml);
    def.content.push({ text: p.doc.title || i18n.t('docSidebar.untitled'), style: 'h1', pageBreak: 'before' });
    def.content.push(...documentBodyContent(html));
  }
  const buffer = await Promise.resolve(pdfMake.createPdf(def).getBuffer());
  return new Uint8Array(buffer);
}

/** Page-link anchors → relative links to the exported HTML files. */
function linkHtmlPages(html, file, paths) {
  return html.replace(/<a\b[^>]*\bdata-doc-link="([^"]+)"[^>]*>/g, (tag, id) => {
    const target = paths.get(String(id));
    return target ? `<a href="${relativeHref(file, target)}">` : tag;
  });
}

/**
 * Export the open document. `snapshot` is its live `{ title, bodyHtml,
 * bodyLexical }` (unsaved edits included). Resolves `{ saved, cancelled? }`.
 */
export async function exportOpenDocument(format, { documentId, scope, snapshot, includeSubpages = true }) {
  const pages = await collectPages({ documentId, scope, snapshot, includeSubpages });
  const base = exportFileBase(snapshot.title);
  const multi = pages.length > 1;

  if (format === 'pdf') {
    if (!multi) return exportSingle('pdf', snapshot);
    return saveBinaryFile({ bytes: await combinedPdf(pages), filename: `${base}.pdf`, mime: 'application/pdf', filters: [{ name: 'PDF', extensions: ['pdf'] }] });
  }
  if (format === 'markdown') {
    if (!multi) {
      return saveTextFile({
        content: markdownFileFor(pages[0].node, pages[0].doc),
        filename: `${base}.md`,
        mime: 'text/markdown',
        filters: [{ name: 'Markdown', extensions: ['md', 'markdown'] }],
      });
    }
    const paths = pagePaths(pages, '.md');
    const entries = pages.map((p) => {
      const file = paths.get(String(p.node.id));
      return { path: file, content: markdownFileFor(p.node, p.doc, { file, paths }) };
    });
    return saveZip(entries, base);
  }
  if (format === 'html') {
    if (!multi) return exportSingle('html', snapshot);
    const paths = pagePaths(pages, '.html');
    const entries = [];
    for (const p of pages) {
      const file = paths.get(String(p.node.id));
      // eslint-disable-next-line no-await-in-loop
      entries.push({ path: file, content: linkHtmlPages(await documentToHtmlFile(p.doc.title, p.doc.bodyHtml), file, paths) });
    }
    return saveZip(entries, base);
  }
  return { saved: false };
}

function saveZip(entries, base) {
  return saveBinaryFile({ bytes: zipStore(entries), filename: `${base}.zip`, mime: 'application/zip', filters: [{ name: 'Zip', extensions: ['zip'] }] });
}
